import React from "react";
import $ from "jquery";
import { Helmet } from "react-helmet-async";
import { Box, Stack, Button } from "@mui/material";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CachedIcon from "@mui/icons-material/Cached";
import DateRangeIcon from "@mui/icons-material/DateRange";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import PersonRemoveAlt1OutlinedIcon from "@mui/icons-material/PersonRemoveAlt1Outlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";

import "../../../vendor/react-minimal-datetime-range.css";

import useUser from "../../../hooks/super/useUser";
import useAuth from "../../../hooks/useAuth";

import NewUserList from "./component/T_NewUser";
import DeleteUserList from "./component/T_DeleteUser";
import ActiveUserList from "./component/T_ActiveUser";

import { CollapseAlert, MenuItem, RangePicker } from "../../../components/pages/common/styled";
import { StyledMenu } from "../../../components/pages/application/analytics/styled";

import PageHeader from "../../../components/pages/_new/super/common/PageHeader";
import OutlinedButton from "../../../components/pages/_new/super/common/OutlinedButton";
import {
  TOKENS,
  SectionCard,
  StatTile,
  EmptyState,
} from "../../../components/pages/_new/super/design";
import { normalizePickerDateRange } from "../../../components/pages/_new/detection/wafEventListUtils";

function SAUserReport() {
  const {
    getNewUserReport,
    getDeleteUserReport,
    getActiveUserReport,
    newSize,
    deleteSize,
    activeSize,
    newTotal,
    deleteTotal,
    activeTotal,
    newUsers,
    deleteUsers,
    activeUsers,
    setErr,
    errMsg,
  } = useUser();
  const { isAuthenticated } = useAuth();
  const [errOpen, setErrOpen] = React.useState(false);
  const [timeRange, setTimeRange] = React.useState("3M");
  const [customRangeConfirm, setCustomRangeConfirm] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const now = new Date();
  const oneDayAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const date = String(now.getDate()).padStart(2, "0");
  const year = String(now.getFullYear());
  const _month = String(oneDayAgo.getMonth() + 1).padStart(2, "0");
  const _date = String(oneDayAgo.getDate()).padStart(2, "0");
  const _year = String(oneDayAgo.getFullYear());
  const timeZone =
    now.getTimezoneOffset() >= 0
      ? "-" + String(now.getTimezoneOffset() / 60).padStart(2, "0") + ":00"
      : "+" + String(now.getTimezoneOffset() / -60).padStart(2, "0") + ":00";
  const [customDateRange, setCustomDateRange] = React.useState([
    _year + "-" + _month + "-" + _date + "T00:00:00",
    year + "-" + month + "-" + date + "T00:00:00",
  ]);

  const getPeriodString = (period) => {
    switch (period) {
      case "1d":
        return "Last 24 Hours";
      case "7d":
        return "Last 7 Days";
      case "3M":
        return "Last 3 Months";
      case "custom":
        return (
          customDateRange[0].split("T")[0] +
          " " +
          customDateRange[0].split("T")[1] +
          " - " +
          customDateRange[1].split("T")[0] +
          " " +
          customDateRange[1].split("T")[1]
        );
      default:
        return "Last 7 Days";
    }
  };
  const getTimeRange = () => {
    if (timeRange === "custom") {
      return { time_zone: timeZone, from: customDateRange[0], to: customDateRange[1] };
    }
    return { period: timeRange };
  };
  const selectPeriod = (period) => {
    setAnchorEl(null);
    setTimeRange(period);
    if (period === "custom") {
      $(document).ready(function () {
        $(".react-minimal-datetime-range__range-input-wrapper").click();
        $(".react-minimal-datetime-range").css({
          "margin-top": $(".react-minimal-datetime-range__range-input-wrapper").parent().parent().parent().height() + 4,
        });
      });
    }
  };
  const selectCustomDateRange = (res) => {
    setCustomRangeConfirm(true);
    setCustomDateRange(normalizePickerDateRange(res));
  };
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const refresh = () => {
    getNewUserReport(getTimeRange(), newSize, 0, true);
    getDeleteUserReport(getTimeRange(), deleteSize, 0, true);
    getActiveUserReport(getTimeRange(), activeSize, 0, true);
  };

  React.useEffect(() => {
    if (!isAuthenticated) return;
    if (timeRange === "custom" && customRangeConfirm === false) return;
    setCustomRangeConfirm(false);
    getNewUserReport(getTimeRange(), newSize, 0, true);
    getDeleteUserReport(getTimeRange(), deleteSize, 0, true);
    getActiveUserReport(getTimeRange(), activeSize, 0, true);
  }, [isAuthenticated, timeRange, customDateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    setErrOpen(true);
  }, [errMsg]);

  const fmt = (n) => (typeof n === "number" ? n.toLocaleString() : "0");
  const isEmpty = (list) => Array.isArray(list) && list.length === 0;
  const periodLabel = getPeriodString(timeRange);

  return (
    <React.Fragment>
      <Helmet title="SA User Report" />
      <PageHeader
        title="User Report"
        description="Activity, sign-ups and deletions across the selected time window"
        actions={
          <>
            <Button
              id="timeRangeButton"
              onClick={handleClick}
              startIcon={<DateRangeIcon sx={{ width: 18, height: 18 }} />}
              endIcon={<ArrowDropDownIcon sx={{ color: TOKENS.fg2 }} />}
              sx={{
                textTransform: "none",
                fontWeight: 400,
                fontSize: 14,
                lineHeight: "21px",
                height: 36,
                px: 2,
                minWidth: 240,
                borderRadius: "8px",
                border: `1px solid ${TOKENS.border}`,
                background: TOKENS.bgSurface,
                color: TOKENS.fg1,
                justifyContent: "space-between",
                "& .MuiButton-startIcon": { color: TOKENS.fg2, mr: 1 },
                "&:hover": {
                  background: TOKENS.bgSunken,
                  borderColor: TOKENS.borderStrong,
                },
              }}
            >
              <Box
                component="span"
                sx={{
                  flex: 1,
                  textAlign: "left",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {periodLabel}
              </Box>
            </Button>
            <OutlinedButton onClick={refresh} startIcon={<CachedIcon style={{ width: 20, height: 20 }} />}>
              Refresh
            </OutlinedButton>
          </>
        }
      />

      {/* Hidden RangePicker — only opens when the Custom menu item is chosen.
          Keeps the vendored library's date-range UI but stays visually invisible
          in the toolbar so the styled Button is the sole trigger surface. */}
      <Box sx={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}>
        <RangePicker
          locale="en-us"
          show={false}
          disabled={false}
          allowPageClickToClose={true}
          onConfirm={(res) => selectCustomDateRange(res)}
          placeholder={["Start Time", "End Time"]}
          showOnlyTime={false}
          defaultDates={[customDateRange[0].split("T")[0], customDateRange[1].split("T")[0]]}
          defaultTimes={[customDateRange[0].split("T")[1].substr(0, 5), customDateRange[1].split("T")[1].substr(0, 5)]}
          initialDates={[customDateRange[0].split("T")[0], customDateRange[1].split("T")[0]]}
          initialTimes={[customDateRange[0].split("T")[1].substr(0, 5), customDateRange[1].split("T")[1].substr(0, 5)]}
        />
      </Box>

      <Box sx={{ mb: 4 }}>
        <CollapseAlert errOpen={errOpen} setErrOpen={setErrOpen} setErr={setErr} errMsg={errMsg} />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
          gap: 2,
          mb: 4,
        }}
      >
        <StatTile
          icon={<PeopleAltOutlinedIcon />}
          value={fmt(activeTotal)}
          label="Active users"
          sub={periodLabel}
        />
        <StatTile
          icon={<PersonAddAlt1OutlinedIcon />}
          iconBg={TOKENS.successBg}
          iconInk={TOKENS.successInk}
          value={fmt(newTotal)}
          label="New sign-ups"
          sub={periodLabel}
        />
        <StatTile
          icon={<PersonRemoveAlt1OutlinedIcon />}
          iconBg={TOKENS.dangerBg}
          iconInk={TOKENS.dangerInk}
          value={fmt(deleteTotal)}
          label="Deleted accounts"
          sub={periodLabel}
        />
      </Box>

      <Stack spacing={3}>
        <SectionCard
          title="Active users"
          description="Returning users in the selected window"
        >
          {isEmpty(activeUsers) ? (
            <EmptyState
              icon={<InboxOutlinedIcon />}
              title="No active users in this window"
              body="Pick a longer range from the time picker, or refresh once data lands."
            />
          ) : (
            <ActiveUserList />
          )}
        </SectionCard>

        <SectionCard
          title="New users"
          description="Sign-ups in the selected window"
        >
          {isEmpty(newUsers) ? (
            <EmptyState
              icon={<InboxOutlinedIcon />}
              title="No sign-ups in this window"
              body="No new accounts were created in the selected time range."
            />
          ) : (
            <NewUserList />
          )}
        </SectionCard>

        <SectionCard
          title="Deleted users"
          description="Accounts removed in the selected window"
        >
          {isEmpty(deleteUsers) ? (
            <EmptyState
              icon={<InboxOutlinedIcon />}
              title="No deletions in this window"
              body="No accounts were removed in the selected time range."
            />
          ) : (
            <DeleteUserList />
          )}
        </SectionCard>
      </Stack>

      <StyledMenu
        id="demo-customized-menu"
        MenuListProps={{ "aria-labelledby": "demo-customized-button" }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <MenuItem onClick={() => selectPeriod("1d")} disableRipple>Last 24 Hours</MenuItem>
        <MenuItem onClick={() => selectPeriod("7d")} disableRipple>Last 7 Days</MenuItem>
        <MenuItem onClick={() => selectPeriod("3M")} disableRipple>Last 3 Months</MenuItem>
        <MenuItem onClick={() => selectPeriod("custom")} disableRipple>Custom ...</MenuItem>
      </StyledMenu>
    </React.Fragment>
  );
}
export default SAUserReport;
